// Construction des rapports de l'espace Agence.
// Toutes les lignes proviennent des données accessibles au compte connecté :
// propriétaires, immeubles, biens, locataires, contrats, loyers, paiements,
// dépenses, maintenance et documents. Aucune donnée fictive n'est ajoutée.
// Le jour où la base de données est branchée, seul le contexte change.

import type {
  ReportFilters,
  ReportResult,
  ReportRow,
  ReportTotal,
  StatutFilterConfig,
} from "@/components/feature/reports/types";
import { inDateRange, inPeriodRange, periodKeyOf } from "@/components/feature/reports/utils";
import { formatDate, formatFcfa } from "@/utils/format";
import type {
  AgenceBien,
  AgenceContrat,
  AgenceDepense,
  AgenceDocument,
  AgenceImmeuble,
  AgenceIntervention,
  AgenceLocataire,
  AgenceLoyer,
  AgencePaiement,
} from "@/pages/agence/data/types";
import {
  AGENCE_BIEN_STATUT_LABELS,
  AGENCE_BIEN_TYPE_LABELS,
  AGENCE_CONTRAT_STATUT_LABELS,
  AGENCE_DEPENSE_CATEGORIE_LABELS,
  AGENCE_DOCUMENT_TYPE_LABELS,
  AGENCE_INTERVENTION_PRIORITE_LABELS,
  AGENCE_INTERVENTION_STATUT_LABELS,
  AGENCE_LOYER_STATUT_LABELS,
  AGENCE_MODE_PAIEMENT_LABELS,
  AGENCE_TYPE_PIECE_LABELS,
  agenceContratReference,
  agenceLocataireNomComplet,
  agencePaiementReference,
  formatAgenceFileSize,
  isAgenceImpaye,
} from "@/pages/agence/data/types";
import { monthLabel } from "@/pages/agence/loyers/periods";
import type { Proprietaire } from "@/pages/agence/proprietaires/types";
import { proprietaireNomComplet, PROPRIETAIRE_STATUT_LABELS } from "@/pages/agence/proprietaires/types";

export interface AgenceReportContext {
  proprietaires: Proprietaire[];
  immeubles: AgenceImmeuble[];
  biens: AgenceBien[];
  locataires: AgenceLocataire[];
  contrats: AgenceContrat[];
  loyers: AgenceLoyer[];
  paiements: AgencePaiement[];
  depenses: AgenceDepense[];
  interventions: AgenceIntervention[];
  documents: AgenceDocument[];
}

const OPTION = (value: string, label: string) => ({ value, label });

/** Filtre contextuel (statut / type / catégorie) propre à chaque rapport. */
export function getAgenceStatutConfig(typeId: string): StatutFilterConfig {
  switch (typeId) {
    case "proprietaires":
      return { label: "Statut", options: Object.entries(PROPRIETAIRE_STATUT_LABELS).map(([value, label]) => OPTION(value, label)) };
    case "biens":
      return { label: "Statut", options: Object.entries(AGENCE_BIEN_STATUT_LABELS).map(([value, label]) => OPTION(value, label)) };
    case "contrats":
      return { label: "Statut", options: Object.entries(AGENCE_CONTRAT_STATUT_LABELS).map(([value, label]) => OPTION(value, label)) };
    case "loyers":
      return { label: "Statut", options: Object.entries(AGENCE_LOYER_STATUT_LABELS).map(([value, label]) => OPTION(value, label)) };
    case "impayes":
      return { label: "Statut", options: [OPTION("en-retard", "En retard"), OPTION("impaye", "Impayé")] };
    case "paiements":
      return { label: "Mode de paiement", options: Object.entries(AGENCE_MODE_PAIEMENT_LABELS).map(([value, label]) => OPTION(value, label)) };
    case "depenses":
      return { label: "Catégorie", options: Object.entries(AGENCE_DEPENSE_CATEGORIE_LABELS).map(([value, label]) => OPTION(value, label)) };
    case "maintenance":
      return { label: "Statut", options: Object.entries(AGENCE_INTERVENTION_STATUT_LABELS).map(([value, label]) => OPTION(value, label)) };
    case "documents":
      return { label: "Type de document", options: Object.entries(AGENCE_DOCUMENT_TYPE_LABELS).map(([value, label]) => OPTION(value, label)) };
    default:
      return { label: "Statut", options: [] };
  }
}

/* ---------------------------------------------------------------- */
/* Aides de résolution et de filtrage                                */
/* ---------------------------------------------------------------- */

const proprietaireNom = (ctx: AgenceReportContext, id: string) => {
  const proprietaire = ctx.proprietaires.find((item) => item.id === id);
  return proprietaire ? proprietaireNomComplet(proprietaire) : "—";
};

const immeubleNom = (ctx: AgenceReportContext, id: string) => ctx.immeubles.find((item) => item.id === id)?.nom ?? "—";

const bienLabel = (ctx: AgenceReportContext, id: string) => {
  const bien = ctx.biens.find((item) => item.id === id);
  return bien ? `${AGENCE_BIEN_TYPE_LABELS[bien.type]}${bien.reference ? ` ${bien.reference}` : ""}` : "—";
};

const locataireNom = (ctx: AgenceReportContext, id: string) => {
  const locataire = ctx.locataires.find((item) => item.id === id);
  return locataire ? agenceLocataireNomComplet(locataire) : "—";
};

const total = (label: string, value: string, tone: ReportTotal["tone"] = "neutral"): ReportTotal => ({ label, value, tone });

const matchProprietaire = (id: string, f: ReportFilters) => !f.proprietaireId || id === f.proprietaireId;
const matchImmeuble = (immeubleId: string, f: ReportFilters) => !f.immeubleId || immeubleId === f.immeubleId;
const matchBien = (bienId: string, f: ReportFilters) => !f.bienId || bienId === f.bienId;
const matchLocataire = (locataireId: string, f: ReportFilters) => !f.locataireId || locataireId === f.locataireId;

const sumFcfa = (values: number[]) => formatFcfa(values.reduce((sum, value) => sum + value, 0));

/* ---------------------------------------------------------------- */
/* Rapports                                                          */
/* ---------------------------------------------------------------- */

function reportProprietaires(ctx: AgenceReportContext, f: ReportFilters): ReportResult {
  const filtered = ctx.proprietaires
    .filter((proprietaire) => matchProprietaire(proprietaire.id, f))
    .filter((proprietaire) => !f.statut || proprietaire.statut === f.statut)
    .filter((proprietaire) => inDateRange(proprietaire.createdAt, f.dateFrom, f.dateTo));

  const ownerImmeubles = (id: string) => ctx.immeubles.filter((item) => item.proprietaireId === id);
  const ownerBiens = (id: string) => ctx.biens.filter((item) => item.proprietaireId === id);
  const ownerContrats = (id: string) => ctx.contrats.filter((item) => item.proprietaireId === id);
  const ownerEncaisse = (id: string) => {
    const contratIds = new Set(ownerContrats(id).map((contrat) => contrat.id));
    return ctx.paiements.filter((paiement) => contratIds.has(paiement.contratId)).reduce((sum, p) => sum + p.montant, 0);
  };

  const rows = filtered.map<ReportRow>((proprietaire) => ({
    nom: proprietaireNomComplet(proprietaire),
    telephone: proprietaire.telephone || "—",
    email: proprietaire.email || "—",
    statut: PROPRIETAIRE_STATUT_LABELS[proprietaire.statut],
    immeubles: ownerImmeubles(proprietaire.id).length,
    biens: ownerBiens(proprietaire.id).length,
    contrats: ownerContrats(proprietaire.id).length,
    encaisse: formatFcfa(ownerEncaisse(proprietaire.id)),
  }));

  return {
    title: "Rapport des propriétaires",
    description: "Propriétaires gérés par l'agence avec leur portefeuille.",
    columns: [
      { key: "nom", header: "Propriétaire" },
      { key: "telephone", header: "Téléphone" },
      { key: "email", header: "Email" },
      { key: "statut", header: "Statut" },
      { key: "immeubles", header: "Immeubles", align: "right" },
      { key: "biens", header: "Biens", align: "right" },
      { key: "contrats", header: "Contrats", align: "right" },
      { key: "encaisse", header: "Encaissements", align: "right" },
    ],
    rows,
    totals: [
      total("Propriétaires", String(filtered.length), "primary"),
      total("Immeubles gérés", String(filtered.reduce((sum, p) => sum + ownerImmeubles(p.id).length, 0)), "secondary"),
      total("Biens gérés", String(filtered.reduce((sum, p) => sum + ownerBiens(p.id).length, 0)), "accent"),
    ],
  };
}

function reportImmeubles(ctx: AgenceReportContext, f: ReportFilters): ReportResult {
  const filtered = ctx.immeubles
    .filter((immeuble) => matchProprietaire(immeuble.proprietaireId, f) && matchImmeuble(immeuble.id, f))
    .filter((immeuble) => inDateRange(immeuble.createdAt, f.dateFrom, f.dateTo));
  const biensCount = (id: string) => ctx.biens.filter((bien) => bien.immeubleId === id).length;
  const locatairesCount = (id: string) =>
    new Set(ctx.contrats.filter((contrat) => contrat.immeubleId === id).map((contrat) => contrat.locataireId)).size;

  const rows = filtered.map<ReportRow>((immeuble) => ({
    nom: immeuble.nom,
    proprietaire: proprietaireNom(ctx, immeuble.proprietaireId),
    localisation: [immeuble.region, immeuble.commune].filter(Boolean).join(" · ") || "—",
    adresse: immeuble.adresse || "—",
    niveaux: immeuble.niveaux ?? 0,
    biens: biensCount(immeuble.id),
    locataires: locatairesCount(immeuble.id),
  }));

  return {
    title: "Rapport des immeubles",
    description: "Immeubles gérés par l'agence, par propriétaire.",
    columns: [
      { key: "nom", header: "Immeuble" },
      { key: "proprietaire", header: "Propriétaire" },
      { key: "localisation", header: "Localisation" },
      { key: "adresse", header: "Adresse" },
      { key: "niveaux", header: "Niveaux", align: "right" },
      { key: "biens", header: "Biens", align: "right" },
      { key: "locataires", header: "Locataires", align: "right" },
    ],
    rows,
    totals: [
      total("Immeubles", String(filtered.length), "primary"),
      total("Biens rattachés", String(filtered.reduce((sum, immeuble) => sum + biensCount(immeuble.id), 0)), "secondary"),
      total("Locataires rattachés", String(filtered.reduce((sum, immeuble) => sum + locatairesCount(immeuble.id), 0)), "accent"),
    ],
  };
}

function reportBiens(ctx: AgenceReportContext, f: ReportFilters): ReportResult {
  const filtered = ctx.biens
    .filter((bien) => matchProprietaire(bien.proprietaireId, f) && matchImmeuble(bien.immeubleId, f) && matchBien(bien.id, f))
    .filter((bien) => !f.statut || bien.statut === f.statut)
    .filter((bien) => inDateRange(bien.createdAt, f.dateFrom, f.dateTo));

  const rows = filtered.map<ReportRow>((bien) => ({
    type: AGENCE_BIEN_TYPE_LABELS[bien.type],
    reference: bien.reference || "—",
    proprietaire: proprietaireNom(ctx, bien.proprietaireId),
    immeuble: immeubleNom(ctx, bien.immeubleId),
    emplacement: [bien.etage, bien.numero].filter(Boolean).join(" · ") || "—",
    surface: bien.surface ? `${bien.surface} m²` : "—",
    loyer: formatFcfa(bien.loyerMensuel),
    statut: AGENCE_BIEN_STATUT_LABELS[bien.statut],
  }));

  return {
    title: "Rapport des biens",
    description: "Inventaire des biens gérés avec loyer et statut d'occupation.",
    columns: [
      { key: "type", header: "Type" },
      { key: "reference", header: "Référence" },
      { key: "proprietaire", header: "Propriétaire" },
      { key: "immeuble", header: "Immeuble" },
      { key: "emplacement", header: "Emplacement" },
      { key: "surface", header: "Surface", align: "right" },
      { key: "loyer", header: "Loyer mensuel", align: "right" },
      { key: "statut", header: "Statut" },
    ],
    rows,
    totals: [
      total("Biens", String(filtered.length), "primary"),
      total("Loyer mensuel cumulé", sumFcfa(filtered.map((bien) => bien.loyerMensuel)), "secondary"),
      total("Occupés", String(filtered.filter((bien) => bien.statut === "occupe").length), "accent"),
    ],
  };
}

function reportLocataires(ctx: AgenceReportContext, f: ReportFilters): ReportResult {
  const filtered = ctx.locataires
    .filter((locataire) => matchLocataire(locataire.id, f))
    .filter((locataire) => {
      if (!f.proprietaireId && !f.immeubleId && !f.bienId) return true;
      return ctx.contrats.some(
        (contrat) =>
          contrat.locataireId === locataire.id &&
          matchProprietaire(contrat.proprietaireId, f) &&
          matchImmeuble(contrat.immeubleId, f) &&
          matchBien(contrat.bienId, f)
      );
    })
    .filter((locataire) => inDateRange(locataire.createdAt, f.dateFrom, f.dateTo));

  const rows = filtered.map<ReportRow>((locataire) => {
    const contrat = ctx.contrats.find((item) => item.locataireId === locataire.id);
    return {
      nom: agenceLocataireNomComplet(locataire),
      telephone: locataire.telephone || "—",
      email: locataire.email || "—",
      profession: locataire.profession || "—",
      piece: locataire.typePiece ? AGENCE_TYPE_PIECE_LABELS[locataire.typePiece] : "—",
      logement: contrat ? bienLabel(ctx, contrat.bienId) : "—",
      immeuble: contrat ? immeubleNom(ctx, contrat.immeubleId) : "—",
    };
  });

  return {
    title: "Rapport des locataires",
    description: "Répertoire des locataires gérés et de leur logement.",
    columns: [
      { key: "nom", header: "Locataire" },
      { key: "telephone", header: "Téléphone" },
      { key: "email", header: "Email" },
      { key: "profession", header: "Profession" },
      { key: "piece", header: "Pièce" },
      { key: "logement", header: "Logement" },
      { key: "immeuble", header: "Immeuble" },
    ],
    rows,
    totals: [total("Locataires", String(filtered.length), "primary")],
  };
}

function reportContrats(ctx: AgenceReportContext, f: ReportFilters): ReportResult {
  const filtered = ctx.contrats
    .filter((contrat) => matchProprietaire(contrat.proprietaireId, f) && matchImmeuble(contrat.immeubleId, f) && matchBien(contrat.bienId, f))
    .filter((contrat) => matchLocataire(contrat.locataireId, f))
    .filter((contrat) => !f.statut || contrat.statut === f.statut)
    .filter((contrat) => inDateRange(contrat.dateDebut, f.dateFrom, f.dateTo));

  const rows = filtered.map<ReportRow>((contrat) => ({
    reference: agenceContratReference(contrat.id),
    proprietaire: proprietaireNom(ctx, contrat.proprietaireId),
    immeuble: immeubleNom(ctx, contrat.immeubleId),
    bien: bienLabel(ctx, contrat.bienId),
    locataire: locataireNom(ctx, contrat.locataireId),
    debut: contrat.dateDebut ? formatDate(contrat.dateDebut) : "—",
    fin: contrat.dateFin ? formatDate(contrat.dateFin) : "—",
    loyer: formatFcfa(contrat.loyerMensuel),
    statut: AGENCE_CONTRAT_STATUT_LABELS[contrat.statut],
  }));

  return {
    title: "Rapport des contrats",
    description: "Contrats de location avec loyer, caution et statut.",
    columns: [
      { key: "reference", header: "Référence" },
      { key: "proprietaire", header: "Propriétaire" },
      { key: "immeuble", header: "Immeuble" },
      { key: "bien", header: "Bien" },
      { key: "locataire", header: "Locataire" },
      { key: "debut", header: "Début" },
      { key: "fin", header: "Fin" },
      { key: "loyer", header: "Loyer mensuel", align: "right" },
      { key: "statut", header: "Statut" },
    ],
    rows,
    totals: [
      total("Contrats", String(filtered.length), "primary"),
      total("Loyer mensuel cumulé", sumFcfa(filtered.map((contrat) => contrat.loyerMensuel)), "secondary"),
      total("Actifs", String(filtered.filter((contrat) => contrat.statut === "actif").length), "accent"),
    ],
  };
}

function reportLoyers(ctx: AgenceReportContext, f: ReportFilters): ReportResult {
  const filtered = ctx.loyers
    .filter((loyer) => matchProprietaire(loyer.proprietaireId, f) && matchImmeuble(loyer.immeubleId, f) && matchBien(loyer.bienId, f))
    .filter((loyer) => matchLocataire(loyer.locataireId, f))
    .filter((loyer) => !f.statut || loyer.statut === f.statut)
    .filter((loyer) => inPeriodRange(loyer.periode, f.dateFrom, f.dateTo));

  const rows = filtered.map<ReportRow>((loyer) => ({
    periode: monthLabel(loyer.periode),
    proprietaire: proprietaireNom(ctx, loyer.proprietaireId),
    locataire: locataireNom(ctx, loyer.locataireId),
    bien: bienLabel(ctx, loyer.bienId),
    montant: formatFcfa(loyer.montant),
    echeance: loyer.echeance ? formatDate(loyer.echeance) : "—",
    paye: formatFcfa(loyer.totalPaye),
    reste: formatFcfa(loyer.reste),
    statut: AGENCE_LOYER_STATUT_LABELS[loyer.statut],
  }));

  return {
    title: "Rapport des loyers",
    description: "Échéances de loyer avec montants dus, encaissés et reste à payer.",
    columns: [
      { key: "periode", header: "Période" },
      { key: "proprietaire", header: "Propriétaire" },
      { key: "locataire", header: "Locataire" },
      { key: "bien", header: "Bien" },
      { key: "montant", header: "Montant", align: "right" },
      { key: "echeance", header: "Échéance" },
      { key: "paye", header: "Encaissé", align: "right" },
      { key: "reste", header: "Reste", align: "right" },
      { key: "statut", header: "Statut" },
    ],
    rows,
    totals: [
      total("Échéances", String(filtered.length), "primary"),
      total("Montant total", sumFcfa(filtered.map((loyer) => loyer.montant)), "secondary"),
      total("Encaissé", sumFcfa(filtered.map((loyer) => loyer.totalPaye)), "accent"),
      total("Reste à percevoir", sumFcfa(filtered.map((loyer) => loyer.reste)), "neutral"),
    ],
  };
}

function reportPaiements(ctx: AgenceReportContext, f: ReportFilters): ReportResult {
  const contratOf = (paiement: AgencePaiement) => ctx.contrats.find((item) => item.id === paiement.contratId);

  const filtered = ctx.paiements
    .filter((paiement) => {
      const contrat = contratOf(paiement);
      if (!contrat) return !f.proprietaireId && !f.immeubleId && !f.bienId && !f.locataireId;
      return (
        matchProprietaire(contrat.proprietaireId, f) &&
        matchImmeuble(contrat.immeubleId, f) &&
        matchBien(contrat.bienId, f) &&
        matchLocataire(contrat.locataireId, f)
      );
    })
    .filter((paiement) => !f.statut || paiement.mode === f.statut)
    .filter((paiement) => inDateRange(paiement.date, f.dateFrom, f.dateTo));

  const rows = filtered.map<ReportRow>((paiement) => {
    const contrat = contratOf(paiement);
    return {
      date: paiement.date ? formatDate(paiement.date) : "—",
      proprietaire: contrat ? proprietaireNom(ctx, contrat.proprietaireId) : "—",
      locataire: contrat ? locataireNom(ctx, contrat.locataireId) : "—",
      periode: paiement.periode ? monthLabel(paiement.periode) : "—",
      montant: formatFcfa(paiement.montant),
      mode: AGENCE_MODE_PAIEMENT_LABELS[paiement.mode],
      reference: paiement.reference || agencePaiementReference(paiement.id),
    };
  });

  return {
    title: "Rapport des paiements",
    description: "Encaissements par date, mode de paiement et locataire.",
    columns: [
      { key: "date", header: "Date" },
      { key: "proprietaire", header: "Propriétaire" },
      { key: "locataire", header: "Locataire" },
      { key: "periode", header: "Période" },
      { key: "montant", header: "Montant", align: "right" },
      { key: "mode", header: "Mode de paiement" },
      { key: "reference", header: "Référence" },
    ],
    rows,
    totals: [
      total("Paiements", String(filtered.length), "primary"),
      total("Montant total", sumFcfa(filtered.map((paiement) => paiement.montant)), "secondary"),
    ],
  };
}

function reportImpayes(ctx: AgenceReportContext, f: ReportFilters): ReportResult {
  const filtered = ctx.loyers
    .filter((loyer) => isAgenceImpaye(loyer.statut))
    .filter((loyer) => matchProprietaire(loyer.proprietaireId, f) && matchImmeuble(loyer.immeubleId, f) && matchBien(loyer.bienId, f))
    .filter((loyer) => matchLocataire(loyer.locataireId, f))
    .filter((loyer) => !f.statut || loyer.statut === f.statut)
    .filter((loyer) => inPeriodRange(loyer.periode, f.dateFrom, f.dateTo));

  const rows = filtered.map<ReportRow>((loyer) => ({
    proprietaire: proprietaireNom(ctx, loyer.proprietaireId),
    locataire: locataireNom(ctx, loyer.locataireId),
    immeuble: immeubleNom(ctx, loyer.immeubleId),
    bien: bienLabel(ctx, loyer.bienId),
    periode: monthLabel(loyer.periode),
    echeance: loyer.echeance ? formatDate(loyer.echeance) : "—",
    montant: formatFcfa(loyer.montant),
    reste: formatFcfa(loyer.reste),
    retard: `${loyer.joursRetard} j`,
    statut: AGENCE_LOYER_STATUT_LABELS[loyer.statut],
  }));

  return {
    title: "Rapport des impayés",
    description: "Loyers en retard ou impayés et reste à recouvrer.",
    columns: [
      { key: "proprietaire", header: "Propriétaire" },
      { key: "locataire", header: "Locataire" },
      { key: "immeuble", header: "Immeuble" },
      { key: "bien", header: "Bien" },
      { key: "periode", header: "Période" },
      { key: "echeance", header: "Échéance" },
      { key: "montant", header: "Montant", align: "right" },
      { key: "reste", header: "Reste dû", align: "right" },
      { key: "retard", header: "Retard", align: "right" },
      { key: "statut", header: "Statut" },
    ],
    rows,
    totals: [
      total("Échéances impayées", String(filtered.length), "primary"),
      total("Reste dû", sumFcfa(filtered.map((loyer) => loyer.reste)), "accent"),
      total("Dont impayés", String(filtered.filter((loyer) => loyer.statut === "impaye").length), "neutral"),
    ],
  };
}

function reportDepenses(ctx: AgenceReportContext, f: ReportFilters): ReportResult {
  const filtered = ctx.depenses
    .filter((depense) => matchProprietaire(depense.proprietaireId, f) && matchImmeuble(depense.immeubleId, f) && matchBien(depense.bienId, f))
    .filter((depense) => !f.statut || depense.categorie === f.statut)
    .filter((depense) => inDateRange(depense.date, f.dateFrom, f.dateTo));

  const rows = filtered.map<ReportRow>((depense) => ({
    date: depense.date ? formatDate(depense.date) : "—",
    categorie: AGENCE_DEPENSE_CATEGORIE_LABELS[depense.categorie],
    proprietaire: proprietaireNom(ctx, depense.proprietaireId),
    immeuble: immeubleNom(ctx, depense.immeubleId),
    bien: depense.bienId ? bienLabel(ctx, depense.bienId) : "—",
    montant: formatFcfa(depense.montant),
    description: depense.description || "—",
  }));

  return {
    title: "Rapport des dépenses",
    description: "Charges par catégorie, propriétaire, immeuble et bien.",
    columns: [
      { key: "date", header: "Date" },
      { key: "categorie", header: "Catégorie" },
      { key: "proprietaire", header: "Propriétaire" },
      { key: "immeuble", header: "Immeuble" },
      { key: "bien", header: "Bien" },
      { key: "montant", header: "Montant", align: "right" },
      { key: "description", header: "Description" },
    ],
    rows,
    totals: [
      total("Dépenses", String(filtered.length), "primary"),
      total("Montant total", sumFcfa(filtered.map((depense) => depense.montant)), "accent"),
    ],
  };
}

function reportMaintenance(ctx: AgenceReportContext, f: ReportFilters): ReportResult {
  const filtered = ctx.interventions
    .filter((intervention) => matchProprietaire(intervention.proprietaireId, f) && matchImmeuble(intervention.immeubleId, f) && matchBien(intervention.bienId, f))
    .filter((intervention) => !f.statut || intervention.statut === f.statut)
    .filter((intervention) => inDateRange(intervention.date, f.dateFrom, f.dateTo));

  const rows = filtered.map<ReportRow>((intervention) => ({
    date: intervention.date ? formatDate(intervention.date) : "—",
    probleme: intervention.probleme || "—",
    proprietaire: proprietaireNom(ctx, intervention.proprietaireId),
    immeuble: immeubleNom(ctx, intervention.immeubleId),
    bien: intervention.bienId ? bienLabel(ctx, intervention.bienId) : "—",
    priorite: AGENCE_INTERVENTION_PRIORITE_LABELS[intervention.priorite],
    statut: AGENCE_INTERVENTION_STATUT_LABELS[intervention.statut],
    cout: formatFcfa(intervention.cout),
  }));

  return {
    title: "Rapport de maintenance",
    description: "Interventions par priorité, statut, prestataire et coût.",
    columns: [
      { key: "date", header: "Date" },
      { key: "probleme", header: "Problème" },
      { key: "proprietaire", header: "Propriétaire" },
      { key: "immeuble", header: "Immeuble" },
      { key: "bien", header: "Bien" },
      { key: "priorite", header: "Priorité" },
      { key: "statut", header: "Statut" },
      { key: "cout", header: "Coût", align: "right" },
    ],
    rows,
    totals: [
      total("Interventions", String(filtered.length), "primary"),
      total("Coût total", sumFcfa(filtered.map((intervention) => intervention.cout)), "accent"),
      total("Terminées", String(filtered.filter((intervention) => intervention.statut === "termine").length), "secondary"),
    ],
  };
}

function reportDocuments(ctx: AgenceReportContext, f: ReportFilters): ReportResult {
  const filtered = ctx.documents
    .filter((document) => matchProprietaire(document.proprietaireId, f) && matchImmeuble(document.immeubleId, f) && matchBien(document.bienId, f))
    .filter((document) => !f.statut || document.type === f.statut)
    .filter((document) => inDateRange(document.date, f.dateFrom, f.dateTo));

  const rows = filtered.map<ReportRow>((document) => ({
    date: document.date ? formatDate(document.date) : "—",
    nom: document.nom || "—",
    type: AGENCE_DOCUMENT_TYPE_LABELS[document.type],
    proprietaire: proprietaireNom(ctx, document.proprietaireId),
    immeuble: immeubleNom(ctx, document.immeubleId),
    bien: document.bienId ? bienLabel(ctx, document.bienId) : "—",
    taille: formatAgenceFileSize(document.fichierTaille),
  }));

  return {
    title: "Rapport des documents",
    description: "Bibliothèque de documents par type et rattachement.",
    columns: [
      { key: "date", header: "Date" },
      { key: "nom", header: "Document" },
      { key: "type", header: "Type" },
      { key: "proprietaire", header: "Propriétaire" },
      { key: "immeuble", header: "Immeuble" },
      { key: "bien", header: "Bien" },
      { key: "taille", header: "Taille", align: "right" },
    ],
    rows,
    totals: [
      total("Documents", String(filtered.length), "primary"),
      total("Taille totale", formatAgenceFileSize(filtered.reduce((sum, document) => sum + (document.fichierTaille || 0), 0)), "secondary"),
    ],
  };
}

function reportRevenus(ctx: AgenceReportContext, f: ReportFilters): ReportResult {
  const loyers = ctx.loyers
    .filter((loyer) => matchProprietaire(loyer.proprietaireId, f) && matchImmeuble(loyer.immeubleId, f) && matchBien(loyer.bienId, f))
    .filter((loyer) => matchLocataire(loyer.locataireId, f))
    .filter((loyer) => inPeriodRange(loyer.periode, f.dateFrom, f.dateTo));
  const depenses = ctx.depenses
    .filter((depense) => matchProprietaire(depense.proprietaireId, f) && matchImmeuble(depense.immeubleId, f) && matchBien(depense.bienId, f))
    .filter((depense) => inPeriodRange(periodKeyOf(depense.date), f.dateFrom, f.dateTo));

  const periodes = Array.from(
    new Set([...loyers.map((loyer) => loyer.periode), ...depenses.map((depense) => periodKeyOf(depense.date))])
  )
    .filter(Boolean)
    .sort((a, b) => (a < b ? 1 : -1));

  const rows = periodes.map<ReportRow>((periode) => {
    const loyersPeriode = loyers.filter((loyer) => loyer.periode === periode);
    const depensesPeriode = depenses.filter((depense) => periodKeyOf(depense.date) === periode);
    const attendu = loyersPeriode.reduce((sum, loyer) => sum + loyer.montant, 0);
    const encaisse = loyersPeriode.reduce((sum, loyer) => sum + loyer.totalPaye, 0);
    const impaye = loyersPeriode.reduce((sum, loyer) => sum + loyer.reste, 0);
    const depense = depensesPeriode.reduce((sum, item) => sum + item.montant, 0);
    return {
      periode: monthLabel(periode),
      attendu: formatFcfa(attendu),
      encaisse: formatFcfa(encaisse),
      impaye: formatFcfa(impaye),
      depense: formatFcfa(depense),
      resultat: formatFcfa(encaisse - depense),
    };
  });

  const totalEncaisse = loyers.reduce((sum, loyer) => sum + loyer.totalPaye, 0);
  const totalDepense = depenses.reduce((sum, depense) => sum + depense.montant, 0);

  return {
    title: "Rapport des revenus",
    description: "Loyers attendus, encaissés, impayés, dépenses et résultat net par mois.",
    columns: [
      { key: "periode", header: "Période" },
      { key: "attendu", header: "Loyers attendus", align: "right" },
      { key: "encaisse", header: "Encaissé", align: "right" },
      { key: "impaye", header: "Impayés", align: "right" },
      { key: "depense", header: "Dépenses", align: "right" },
      { key: "resultat", header: "Résultat net", align: "right" },
    ],
    rows,
    totals: [
      total("Loyers attendus", sumFcfa(loyers.map((loyer) => loyer.montant)), "primary"),
      total("Encaissé", formatFcfa(totalEncaisse), "secondary"),
      total("Impayés", sumFcfa(loyers.map((loyer) => loyer.reste)), "accent"),
      total("Résultat net", formatFcfa(totalEncaisse - totalDepense), "neutral"),
    ],
    note: "Le résultat net correspond aux encaissements moins les dépenses de la période.",
  };
}

function reportGlobal(ctx: AgenceReportContext, f: ReportFilters): ReportResult {
  const loyers = ctx.loyers
    .filter((loyer) => matchProprietaire(loyer.proprietaireId, f) && matchImmeuble(loyer.immeubleId, f) && matchBien(loyer.bienId, f))
    .filter((loyer) => matchLocataire(loyer.locataireId, f))
    .filter((loyer) => inPeriodRange(loyer.periode, f.dateFrom, f.dateTo));
  const paiements = ctx.paiements
    .filter((paiement) => {
      const contrat = ctx.contrats.find((item) => item.id === paiement.contratId);
      if (!contrat) return false;
      return (
        matchProprietaire(contrat.proprietaireId, f) &&
        matchImmeuble(contrat.immeubleId, f) &&
        matchBien(contrat.bienId, f) &&
        matchLocataire(contrat.locataireId, f)
      );
    })
    .filter((paiement) => inDateRange(paiement.date, f.dateFrom, f.dateTo));
  const depenses = ctx.depenses
    .filter((depense) => matchProprietaire(depense.proprietaireId, f) && matchImmeuble(depense.immeubleId, f) && matchBien(depense.bienId, f))
    .filter((depense) => inDateRange(depense.date, f.dateFrom, f.dateTo));
  const interventions = ctx.interventions
    .filter((intervention) => matchProprietaire(intervention.proprietaireId, f) && matchImmeuble(intervention.immeubleId, f) && matchBien(intervention.bienId, f))
    .filter((intervention) => inDateRange(intervention.date, f.dateFrom, f.dateTo));
  const documents = ctx.documents
    .filter((document) => matchProprietaire(document.proprietaireId, f) && matchImmeuble(document.immeubleId, f) && matchBien(document.bienId, f))
    .filter((document) => inDateRange(document.date, f.dateFrom, f.dateTo));
  const immeubles = ctx.immeubles.filter((immeuble) => matchProprietaire(immeuble.proprietaireId, f) && matchImmeuble(immeuble.id, f));
  const biens = ctx.biens.filter((bien) => matchProprietaire(bien.proprietaireId, f) && matchImmeuble(bien.immeubleId, f) && matchBien(bien.id, f));
  const contrats = ctx.contrats.filter(
    (contrat) =>
      matchProprietaire(contrat.proprietaireId, f) &&
      matchImmeuble(contrat.immeubleId, f) &&
      matchBien(contrat.bienId, f) &&
      matchLocataire(contrat.locataireId, f)
  );

  const encaisse = loyers.reduce((sum, loyer) => sum + loyer.totalPaye, 0);
  const depense = depenses.reduce((sum, item) => sum + item.montant, 0);

  const rows: ReportRow[] = [
    { indicateur: "Propriétaires", valeur: String(ctx.proprietaires.filter((p) => matchProprietaire(p.id, f)).length) },
    { indicateur: "Immeubles", valeur: String(immeubles.length) },
    { indicateur: "Biens", valeur: String(biens.length) },
    { indicateur: "Biens occupés", valeur: String(biens.filter((bien) => bien.statut === "occupe").length) },
    { indicateur: "Locataires", valeur: String(ctx.locataires.filter((locataire) => matchLocataire(locataire.id, f)).length) },
    { indicateur: "Contrats", valeur: String(contrats.length) },
    { indicateur: "Contrats actifs", valeur: String(contrats.filter((contrat) => contrat.statut === "actif").length) },
    { indicateur: "Échéances de loyer", valeur: String(loyers.length) },
    { indicateur: "Loyers attendus", valeur: sumFcfa(loyers.map((loyer) => loyer.montant)) },
    { indicateur: "Encaissements", valeur: formatFcfa(encaisse) },
    { indicateur: "Impayés", valeur: sumFcfa(loyers.map((loyer) => loyer.reste)) },
    { indicateur: "Dépenses", valeur: formatFcfa(depense) },
    { indicateur: "Résultat net", valeur: formatFcfa(encaisse - depense) },
    { indicateur: "Interventions de maintenance", valeur: String(interventions.length) },
    { indicateur: "Coût de maintenance", valeur: sumFcfa(interventions.map((intervention) => intervention.cout)) },
    { indicateur: "Documents", valeur: String(documents.length) },
    { indicateur: "Paiements enregistrés", valeur: String(paiements.length) },
  ];

  return {
    title: "Rapport global de l'agence",
    description: "Synthèse complète du portefeuille et de l'activité de l'agence sur la période.",
    columns: [
      { key: "indicateur", header: "Indicateur" },
      { key: "valeur", header: "Valeur", align: "right" },
    ],
    rows,
    totals: [
      total("Encaissements", formatFcfa(encaisse), "primary"),
      total("Impayés", sumFcfa(loyers.map((loyer) => loyer.reste)), "accent"),
      total("Dépenses", formatFcfa(depense), "neutral"),
      total("Résultat net", formatFcfa(encaisse - depense), "secondary"),
    ],
  };
}

const BUILDERS: Record<string, (ctx: AgenceReportContext, f: ReportFilters) => ReportResult> = {
  proprietaires: reportProprietaires,
  immeubles: reportImmeubles,
  biens: reportBiens,
  locataires: reportLocataires,
  contrats: reportContrats,
  loyers: reportLoyers,
  paiements: reportPaiements,
  impayes: reportImpayes,
  depenses: reportDepenses,
  maintenance: reportMaintenance,
  documents: reportDocuments,
  revenus: reportRevenus,
  global: reportGlobal,
};

export function buildAgenceReport(typeId: string, filters: ReportFilters, ctx: AgenceReportContext): ReportResult {
  const builder = BUILDERS[typeId] ?? reportGlobal;
  return builder(ctx, filters);
}